import { Stack, StackProps, Typography } from "@mui/material";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { formatNumber, formatString } from "@/shared/formats";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import usePrintBold from "@/features/configuration/hooks/use-print-bold";
import TicketThemeProvider from "@/features/inventario/ventas/components/venta-print-factura/ticket-theme-provider";
import { KudeTicketDatos } from "@/features/inventario/ventas/api/get-venta-kude-ticket-datos";

// Replica el formato del KuDE en PDF (KudeService.Fmt): 0 decimales para Guaraníes,
// 2 para moneda extranjera, y 4 cuando el monto es menor a 1 en valor absoluto.
function formatMonto(value?: number | null, cMoneOpe?: string | null) {
    if (value == null) return formatNumber(undefined);
    const esPequeno = Math.abs(value) > 0 && Math.abs(value) < 1;
    const decimalScale = cMoneOpe === "PYG" ? (esPequeno ? 4 : 0) : (esPequeno ? 4 : 2);
    return formatNumber(value, decimalScale);
}

function getTipoDocumentoAsociadoLabel(tipo?: number | null) {
    switch (tipo) {
        case 1: return "Electrónico";
        case 2: return "Impreso";
        case 3: return "Constancia Electrónica";
        default: return "-";
    }
}

const AUTOFACTURA = 4;
const NOTA_DE_CREDITO = 5;
const NOTA_REMISION = 7;

export interface KudeTicketViewProps extends StackProps {
    datos: KudeTicketDatos,
}

export default function KudeTicketView({
    datos,
    ...props
}: KudeTicketViewProps) {
    const { printPaddingRight } = useConfigurationContext();
    const [printBold] = usePrintBold();
    const emphasisWeight = printBold ? 700 : "bold";

    const esAutoFactura = datos.iTiDE === AUTOFACTURA;
    const esNotaCredito = datos.iTiDE === NOTA_DE_CREDITO;
    const esNotaRemision = datos.iTiDE === NOTA_REMISION;

    return <TicketThemeProvider>
        <Stack gap={2} paddingRight={`${printPaddingRight}mm`} {...props}>
            <Typography variant="body2" textAlign="center" fontWeight={emphasisWeight}>{datos.docLabel}</Typography>

            {datos.logoBase64 && (
                <Stack alignItems="center">
                    <img src={`data:image/png;base64,${datos.logoBase64}`} style={{ maxHeight: 120, maxWidth: "100%", objectFit: "contain" }} />
                </Stack>
            )}

            <Stack gap={0} alignItems="center">
                <Typography variant="body2" textAlign="center" fontWeight={emphasisWeight}>RUC: {datos.rucEmisor}</Typography>
                <Typography variant="body2" textAlign="center">{formatString(datos.nombreEmisor)}</Typography>
                <Typography variant="body2" textAlign="center">{formatString(datos.direccionEmisor)}</Typography>
                <Typography variant="body2" textAlign="center">{formatString(datos.ciudadEmisor)}</Typography>
                {datos.actividadesEconomicas.map((act, i) => (
                    <Typography key={i} variant="body2" textAlign="center">{act}</Typography>
                ))}
            </Stack>

            <DashedDivider />

            {esAutoFactura
                ? <AutofacturaGeneralData datos={datos} />
                : esNotaCredito
                    ? <NotaCreditoGeneralData datos={datos} />
                    : <FacturaGeneralData datos={datos} esNotaRemision={esNotaRemision} />}

            {datos.cdcDocumentoAsociado && (
                <>
                    <DashedDivider />
                    <Stack gap={1}>
                        <AttributeValuePair label="Tipo Doc. Asociado: " value={getTipoDocumentoAsociadoLabel(datos.tipoDocumentoAsociado)} />
                        <AttributeValuePair label="CDC Doc. Asociado: " value={datos.cdcDocumentoAsociado} />
                    </Stack>
                </>
            )}

            <DashedDivider />

            <Stack gap={1}>
                <Stack gap={0}>
                    <Typography variant="body2" fontWeight={emphasisWeight}>DESCRIPCIÓN</Typography>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={emphasisWeight}>COD.</Typography>
                        <Typography variant="body2" fontWeight={emphasisWeight}>CANT.</Typography>
                        {!esNotaRemision && <Typography variant="body2" fontWeight={emphasisWeight}>PRECIO</Typography>}
                    </Stack>
                </Stack>
                <DashedDivider />
                {datos.items.map((item, i) => (
                    <Stack key={i} gap={0}>
                        <Typography variant="body2">{formatString(item.descripcion)}</Typography>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">{formatString(item.codigo)}</Typography>
                            <Typography variant="body2">{formatNumber(item.cantidad ?? undefined)}</Typography>
                            {!esNotaRemision && <Typography variant="body2">{formatMonto(item.precio, datos.cMoneOpe)}</Typography>}
                        </Stack>
                    </Stack>
                ))}
            </Stack>

            {!esNotaRemision && <>
                <DashedDivider />

                <Stack gap={0.5}>
                    <AttributeValuePair label="Total operación: " value={formatMonto(datos.totalOperacion, datos.cMoneOpe)} variant="bold" />
                    {!esAutoFactura && <AttributeValuePair label="Total Gs: " value={formatMonto(datos.totalGs, "PYG")} variant="bold" />}
                </Stack>

                {!esAutoFactura && (
                    <Stack gap={0.5}>
                        <AttributeValuePair label="Total Exento: " value={formatMonto(datos.totalExento, datos.cMoneOpe)} />
                        <AttributeValuePair label="IVA 5%: " value={formatMonto(datos.iva5, datos.cMoneOpe)} />
                        <AttributeValuePair label="IVA 10%: " value={formatMonto(datos.iva10, datos.cMoneOpe)} />
                        <AttributeValuePair label="Total IVA: " value={formatMonto((datos.iva5 ?? 0) + (datos.iva10 ?? 0), datos.cMoneOpe)} variant="bold" />
                    </Stack>
                )}
            </>}

            {datos.qrBase64 && (
                <Stack alignItems="center">
                    <img src={`data:image/png;base64,${datos.qrBase64}`} style={{ maxHeight: 200, maxWidth: "100%", objectFit: "contain" }} />
                </Stack>
            )}

            <Stack alignItems="center">
                <Typography variant="body2" fontWeight={emphasisWeight} textAlign="center" sx={{ wordBreak: "break-word" }}>CDC: {addSpaces(datos.cdc ?? "", 4)}</Typography>
            </Stack>
            <Typography variant="body2" textAlign="center">{datos.urlConsultas}</Typography>
            <Typography variant="body2" textAlign="center">ESTE DOCUMENTO ES UNA REPRESENTACIÓN GRÁFICA DE UN DOCUMENTO ELECTRÓNICO (XML).</Typography>
        </Stack>
    </TicketThemeProvider>
}

function FacturaGeneralData({
    datos,
    esNotaRemision
}: {
    datos: KudeTicketDatos,
    esNotaRemision: boolean
}) {
    return <>
        <Stack gap={1}>
            <AttributeValuePair label="Timbrado: " value={datos.timbrado} />
            <AttributeValuePair label="Inicio de vigencia: " value={datos.inicioVigencia} />
            <AttributeValuePair label="N°: " value={datos.numeroDocumento} variant="bold" />
            <AttributeValuePair label="Fecha/Hora: " value={dayjs(datos.fechaEmision).format("DD-MM-YYYY HH:mm")} />
            {!esNotaRemision && <AttributeValuePair label="Tipo transacción: " value={datos.tipoTransaccion} />}
            {!esNotaRemision && <AttributeValuePair label="Moneda: " value={datos.moneda} />}
            {!esNotaRemision && <AttributeValuePair label="Condición venta: " value={datos.condicionVenta} />}
            {datos.clienteNombre && <AttributeValuePair label="Cliente: " value={datos.clienteNombre} />}
        </Stack>

        <DashedDivider />

        <Stack gap={1}>
            <AttributeValuePair label="Receptor: " value={datos.receptorNombre} />
            <AttributeValuePair label="Documento: " value={datos.receptorDocumento} />
            <AttributeValuePair label="Dirección: " value={datos.receptorDireccion} />
            {datos.receptorTelefono && <AttributeValuePair label="Teléfono: " value={datos.receptorTelefono} />}
        </Stack>

        {esNotaRemision && datos.transporte && (
            <>
                <DashedDivider />
                <Stack gap={1}>
                    <AttributeValuePair label="Tipo transporte: " value={datos.transporte.tipoTransporte} />
                    <AttributeValuePair label="Inicio: " value={datos.transporte.inicioTraslado} />
                    <AttributeValuePair label="Fin: " value={datos.transporte.finTraslado} />
                    <AttributeValuePair label="Vehículo: " value={datos.transporte.vehiculo} />
                    <AttributeValuePair label="N° Identificación: " value={datos.transporte.numeroIdentificacionVehiculo} />
                    <AttributeValuePair label="Ciudad Salida: " value={datos.transporte.ciudadSalida} />
                    <AttributeValuePair label="Ciudad Entrega: " value={datos.transporte.ciudadEntrega} />
                    <AttributeValuePair label="Transportista: " value={datos.transporte.transportista} />
                    <AttributeValuePair label="Ruc/Doc: " value={datos.transporte.rucDocTransportista} />
                    <AttributeValuePair label="Chofer: " value={datos.transporte.nombreChofer} />
                    <AttributeValuePair label="Doc Chofer: " value={datos.transporte.docChofer} />
                </Stack>
            </>
        )}
    </>
}

function AutofacturaGeneralData({
    datos
}: {
    datos: KudeTicketDatos
}) {
    return <Stack gap={1}>
        <AttributeValuePair label="Timbrado: " value={datos.timbrado} />
        <AttributeValuePair label="Inicio de vigencia: " value={datos.inicioVigencia} />
        <AttributeValuePair label="N°: " value={datos.numeroDocumento} variant="bold" />
        <AttributeValuePair label="Fecha/Hora: " value={dayjs(datos.fechaEmision).format("DD-MM-YYYY HH:mm")} />
        <AttributeValuePair label="Vendedor: " value={datos.vendedorNombre} />
        <AttributeValuePair label="Documento: " value={datos.vendedorDocumento} />
    </Stack>
}

function NotaCreditoGeneralData({
    datos
}: {
    datos: KudeTicketDatos
}) {
    return <>
        <Stack gap={1}>
            <AttributeValuePair label="Timbrado: " value={datos.timbrado} />
            <AttributeValuePair label="Inicio de vigencia: " value={datos.inicioVigencia} />
            <AttributeValuePair label="N°: " value={datos.numeroDocumento} variant="bold" />
            <AttributeValuePair label="Fecha/Hora: " value={dayjs(datos.fechaEmision).format("DD-MM-YYYY HH:mm")} />
            <AttributeValuePair label="Moneda: " value={datos.moneda} />
            <AttributeValuePair label="Tipo transacción: " value={datos.tipoTransaccion} />
        </Stack>

        <DashedDivider />

        <Stack gap={1}>
            <AttributeValuePair label="Receptor: " value={datos.receptorNombre} />
            <AttributeValuePair label="Documento: " value={datos.receptorDocumento} />
            <AttributeValuePair label="Dirección: " value={datos.receptorDireccion} />
            {datos.receptorTelefono && <AttributeValuePair label="Teléfono: " value={datos.receptorTelefono} />}
        </Stack>
    </>
}

function AttributeValuePair({
    label,
    value,
    variant = "normal",
    ...props
}: {
    label?: ReactNode,
    value?: ReactNode,
    variant?: "normal" | "bold"
} & StackProps) {
    const [printBold] = usePrintBold();
    const emphasisWeight = printBold ? 700 : "bold";
    return <Stack direction={"row"} justifyContent="space-between" {...props}>
        <Typography fontWeight={variant === "bold" ? emphasisWeight : undefined} variant={"body2"}>{label}</Typography>
        <Typography fontWeight={variant === "bold" ? emphasisWeight : undefined} variant={"body2"}>{value == null || value === "" ? "---" : value}</Typography>
    </Stack>
}

function DashedDivider() {
    return <Stack borderBottom={"1px dashed #000000"} />
}

function addSpaces(input: string, n: number) {
    if (!input) return "";
    return Array.from({ length: Math.ceil(input.length / n) }, (_, i) => input.slice(i * n, i * n + n)).join(" ");
}
